/**
 * api/axiosInstance.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The one HTTP client for SK Company Cash. Every service goes through it; no
 * screen ever calls axios directly.
 *
 * What it does, and why:
 *
 *  1. withCredentials — on web the platform's httpOnly session cookies ride
 *     along automatically. This product owns no login screen; the session was
 *     established in skplatform-fe.
 *
 *  2. Bearer fallback — on Android/iOS there is no shared cookie jar, so the
 *     token handed over by the platform deep link is attached as an
 *     Authorization header. sk-company-cash-api accepts either form.
 *
 *  3. Single-flight silent refresh — a 401 triggers ONE refresh call, however
 *     many requests raced into it, then replays each original request. The
 *     Overview fires five parallel calls on load; without this, an expired
 *     access token would produce five refreshes and four wasted rotations.
 *
 *  4. Idempotency keys — every write on this API is idempotent and reads
 *     `x-idempotency-key`. The interceptor stamps one per POST so a
 *     double-clicked "Approve" or a retried request cannot post twice. This is
 *     the front-end half of a guarantee the backend already enforces.
 *
 *  5. Deep input trimming — users leave stray spaces in reference fields.
 *     Trimming centrally means nothing is ever persisted with them, and the
 *     format-significant fields are skipped explicitly.
 *
 * The base URL is intentionally empty: every service passes an absolute URL
 * from api/endpoints.ts, so there is one place that knows where a service is.
 * ──────────────────────────────────────────────────────────────────────────
 */
import axios, { type AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { Platform_Refresh_Token_Url } from './endpoints';
import { peekAccessToken, isCookieSession, saveAccessToken, clearTokens } from '../storage/tokenStore';

const axiosInstance = axios.create({
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

/* ── Input hygiene ───────────────────────────────────────────────────────
 * Trim every string in a request body once, centrally. Format-significant
 * fields are left untouched — an idempotency key or a document reference is
 * an opaque token, not prose.
 */
const NO_TRIM_KEYS = new Set(['idempotency_key', 'document_ref', 'token', 'refresh_token', 'code']);

function trimDeep(value: unknown, key?: string): unknown {
  if (typeof value === 'string') return key && NO_TRIM_KEYS.has(key) ? value : value.trim();
  if (Array.isArray(value)) return value.map((v) => trimDeep(v));
  if (
    value &&
    typeof value === 'object' &&
    ((value as object).constructor === Object || (value as object).constructor === undefined)
  ) {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>)) {
      out[k] = trimDeep((value as Record<string, unknown>)[k], k);
    }
    return out;
  }
  return value;
}

/**
 * A per-request idempotency key. UUID when the runtime offers one, otherwise a
 * time-and-entropy composite — uniqueness per request is all that is required,
 * since the backend only uses it to collapse duplicates of the SAME write.
 */
function newIdempotencyKey(): string {
  const c = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `cc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

/* ── Request interceptor ─────────────────────────────────────────────────── */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Native only — on web the cookie is already attached and there is no
    // readable token to add.
    const token = peekAccessToken();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);

    const data = config.data;
    const isPlainBody =
      data &&
      typeof data === 'object' &&
      !(typeof FormData !== 'undefined' && data instanceof FormData) &&
      !(typeof Blob !== 'undefined' && data instanceof Blob);
    if (isPlainBody) config.data = trimDeep(data);

    // Stamp writes. A caller that has its own key (a retry of a specific
    // event, for instance) sets the header itself and keeps it.
    const method = (config.method ?? 'get').toLowerCase();
    if ((method === 'post' || method === 'patch') && !config.headers.get('x-idempotency-key')) {
      config.headers.set('x-idempotency-key', newIdempotencyKey());
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/* ── Single-flight silent refresh ────────────────────────────────────────── */
/**
 * A 401 from the refresh endpoint itself is terminal — retrying it would loop.
 */
const NO_REFRESH = ['/auth/refresh', '/auth/logout'];

let refreshInFlight: Promise<void> | null = null;

/** Listeners notified when the session is definitively gone. */
type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler | null = null;

/**
 * Register what happens when refresh fails. SessionContext installs a handler
 * that routes to skplatform-fe; keeping it a callback means this module has no
 * dependency on navigation.
 */
export function setSessionExpiredHandler(handler: SessionExpiredHandler | null): void {
  onSessionExpired = handler;
}

function refreshSession(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = axiosInstance
      .post<{ access_token?: string }>(Platform_Refresh_Token_Url)
      .then(async ({ data }) => {
        // Web gets a fresh httpOnly cookie and no body token. Native is handed
        // the rotated access token to store.
        if (!isCookieSession() && data?.access_token) {
          await saveAccessToken(data.access_token);
        }
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/* ── Response interceptor ────────────────────────────────────────────────── */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status;
    const url = original?.url ?? '';

    const refreshable =
      status === 401 && original && !original._retried && !NO_REFRESH.some((p) => url.includes(p));

    if (refreshable) {
      try {
        await refreshSession();
        original._retried = true;
        return await axiosInstance(original);
      } catch {
        // The refresh itself failed: the session is over. Clear local state and
        // let the app route back to the platform sign-in.
        await clearTokens();
        onSessionExpired?.();
        return Promise.reject(error);
      }
    }

    // Everything else passes through untouched — utils/errorHandler.ts is what
    // turns it into a message a person reads.
    return Promise.reject(error);
  },
);

export default axiosInstance;
